import type { PlaybackSource } from '~/utils/hooks/usePlayableUrl';
import type { SyncZone } from '~/api/types/sync';

/**
 * Helpers puros do motor de reprodução (Stream). Extraídos de `index.tsx`
 * pra serem testáveis isoladamente — NÃO tocam no fluxo de decoder/render
 * (release OMX, Canvas Bridge, object-fit). Só decisões determinísticas de
 * duração/loop/zona/label.
 */

export function getSourceLabel(source: PlaybackSource): string {
  switch (source) {
    case 'cache':
      return '📦 Cache (offline)';
    case 'remote':
      return '🌐 Stream (online)';
    case 'pending':
      return '⏳ Carregando...';
    default:
      return source;
  }
}

export function getOrientationLabel(
  orientation: 'landscape' | 'portrait',
  customWidth?: number | null,
  customHeight?: number | null,
): string {
  if (customWidth && customHeight && customWidth > 0 && customHeight > 0) {
    return `Custom (${customWidth}x${customHeight})`;
  }
  return orientation === 'landscape' ? 'Paisagem' : 'Retrato';
}

/** WebView Samsung reporta 4–13s no canplay antes do metadata estabilizar. */
export function looksProvisionalWebViewDuration(
  actual: number,
  scheduled: number,
): boolean {
  if (!Number.isFinite(actual) || actual <= 0) return false;
  if (!Number.isFinite(scheduled) || scheduled <= 0) return false;
  return actual < 30 && actual < scheduled * 0.35;
}

export function resolveTrustedNaturalDuration(
  apiDurationSec: number | undefined,
  videoDurationSec: number | undefined,
): number {
  const api = apiDurationSec ?? 0;
  const video = videoDurationSec ?? 0;
  if (api > 0 && video > 0 && looksProvisionalWebViewDuration(video, api)) {
    return api;
  }
  if (api > 0 && video > 0) {
    return Math.max(api, video);
  }
  return api || video || 0;
}

export function resolveOverrideLoopDecision(
  scheduledDuration: number,
  actualDuration: number,
): {
  shouldManualLoop: boolean;
  shouldCutShort: boolean;
  effectiveDuration: number;
} {
  const ratio = scheduledDuration / actualDuration;
  const diff = Math.abs(scheduledDuration - actualDuration);

  if (scheduledDuration < actualDuration) {
    if (diff <= 1) {
      return {
        shouldManualLoop: false,
        shouldCutShort: false,
        effectiveDuration: actualDuration,
      };
    }
    return {
      shouldManualLoop: false,
      shouldCutShort: true,
      effectiveDuration: scheduledDuration,
    };
  }
  if (diff <= 1 || ratio < 2) {
    return {
      shouldManualLoop: false,
      shouldCutShort: false,
      effectiveDuration: actualDuration,
    };
  }
  return {
    shouldManualLoop: true,
    shouldCutShort: false,
    effectiveDuration: scheduledDuration,
  };
}

/**
 * Cut-short wall-clock só quando há evidência de playback real.
 * Logtail 2026-06-08 (Renato): polyfill disparou com currentTime=0,
 * readyState=0, wallElapsed=99s → playlist em loop, tela preta.
 */
export function videoPlayedEnoughForCutShortAdvance(
  video: HTMLVideoElement,
  scheduledDuration: number,
): boolean {
  if (video.readyState < 2) return false;
  if (video.ended) return true;
  if (video.currentTime >= scheduledDuration - 0.5) return true;
  return video.currentTime >= Math.min(2, scheduledDuration * 0.5);
}

/** Controle de override de loop do vídeo (cut-short / manual-loop). */
export interface VideoLoopControl {
  shouldCutShort: boolean;
  shouldManualLoop: boolean;
  scheduledDuration: number;
  playbackStartTime: number;
}

/**
 * Progresso (0-100, já clampado) da barra de reprodução. Extraído do
 * `PlaybackProgressBar` pra ser testável sem rAF/DOM. Fontes:
 *  - vídeo: cut-short (currentTime/scheduled), manual-loop (wall-clock desde o
 *    início / scheduled), ou natural (currentTime/duration).
 *  - imagem/stream: wall-clock (now - startedAt) / duração.
 * `nowMs` é injetado (não usa Date.now() interno) → testável de forma determinística.
 */
export function computeProgressPercent(params: {
  mediaType: string;
  video: HTMLVideoElement | null;
  loopControl: VideoLoopControl;
  imageDurationSec: number;
  startedAtMs: number;
  nowMs: number;
}): number {
  const {
    mediaType,
    video,
    loopControl,
    imageDurationSec,
    startedAtMs,
    nowMs,
  } = params;
  const clamp = (p: number): number => Math.min(Math.max(p, 0), 100);

  if (mediaType === 'video') {
    if (!video) return 0;
    const {
      shouldCutShort,
      shouldManualLoop,
      scheduledDuration,
      playbackStartTime,
    } = loopControl;
    // Caso 1: cut-short — escala pelo tempo cortado.
    if (shouldCutShort && scheduledDuration > 0) {
      return clamp((video.currentTime / scheduledDuration) * 100);
    }
    // Caso 2: manual-loop — wall-clock contra a duração agendada.
    if (shouldManualLoop && scheduledDuration > 0 && playbackStartTime > 0) {
      return clamp(
        ((nowMs - playbackStartTime) / 1000 / scheduledDuration) * 100,
      );
    }
    // Caso 3: natural.
    if (video.duration && video.duration !== Infinity) {
      return clamp((video.currentTime / video.duration) * 100);
    }
    return 0;
  }
  // Imagem / stream — wall-clock desde o início do slot.
  const durMs = imageDurationSec * 1000;
  return durMs > 0 ? clamp(((nowMs - startedAtMs) / durMs) * 100) : 0;
}

/** Fonte a desenhar no canvas de freeze na troca de mídia (seamless). */
export type FreezeCandidate =
  | 'heldCanvas'
  | 'nextImage'
  | 'hopFrame'
  | 'lastFrame'
  | 'lastFrameWc'
  | null;

/** Snapshot mínimo de mídia pra decisão de transição. */
export interface FreezeMediaRef {
  id: string;
  type: string;
}

/**
 * Decide QUAL fonte desenhar no canvas de freeze quando a mídia troca — extraído
 * do useLayoutEffect do Stream pra ser testável sem React/DOM. Espelha a ordem
 * de prioridade (Renato 2026-07-04/05; last-frame WebCodecs 2026-07-16):
 *  1. imagem→X → 'heldCanvas' (a imagem anterior já foi capturada no load; imagem
 *     é estática → o frame congelado == o que estava na tela → INVISÍVEL).
 *  2. vídeo→imagem, com a imagem de destino PRÉ-CARREGADA → 'nextImage'
 *     (canvas == imagem real de destino → troca INVISÍVEL).
 *  3. SAINDO de vídeo, com o ÚLTIMO frame do vídeo (WebCodecs) no cache →
 *     'lastFrameWc' (Tizen/webOS; decode por SOFTWARE → NÃO gated por readback).
 *  4. SAINDO de vídeo, com o último frame nativo (drawImage(<video>)) no cache →
 *     'lastFrame' (Android/browser; gated por `isVideoReadbackAllowed`).
 *  senão → null (sem cobertura → preto curto, ex.: 1ª exibição de um vídeo).
 *
 * PARIDADE COM O ANDROID (Renato 2026-07-16): cobrimos a SAÍDA do vídeo com o
 * ÚLTIMO frame do PRÓPRIO vídeo que SAI (`prev`), não com o 1º frame do que entra.
 * Isso (a) zera o preto no fim (o canvas mostra o que já estava na tela) e (b)
 * transforma a troca num corte de CONTEÚDO (canvas último-frame-de-A → vídeo
 * 1º-frame-de-B), que MASCARA a diferença de qualidade canvas×vídeo (o "soco").
 * 'lastFrameWc' e 'lastFrame' são a MESMA ideia (último frame do que sai) em fontes
 * diferentes: WebCodecs (software, Tizen/webOS) vs drawImage nativo (Android/browser).
 * Cada plataforma alimenta só um dos caches → na prática nunca há conflito; a ordem
 * (Wc antes de nativo) é só determinismo. Ambos ficam APÓS 'nextImage' (que é
 * invisível pra vídeo→imagem, estritamente melhor).
 *
 * Fallback 1ª exibição: sem last-frame cacheado ainda → cai em 'heldCanvas'/
 * 'nextImage' se aplicáveis, senão null (preto curto aceito só na 1ª vez).
 */
export function getFreezeCandidateName(params: {
  prev: FreezeMediaRef | null;
  cur: FreezeMediaRef | null;
  heldCanvasReady: boolean;
  nextImageReady: boolean;
  nextImageReadyId: string | null;
  lastFrameCacheHas: (id: string) => boolean;
  lastFrameWcCacheHas: (id: string) => boolean;
  isVideoReadbackAllowed: boolean;
  /**
   * Id do vídeo cujo frame foi capturado NO INSTANTE do corte (hop do pulso de sync).
   * Vence o último-frame-de-fim: num corte no meio do vídeo, o frame de fim mostraria
   * uma cena que a tela nunca chegou a exibir; o do corte é o que ESTAVA na tela.
   */
  hopFrameReadyId?: string | null;
}): FreezeCandidate {
  const {
    prev,
    cur,
    heldCanvasReady,
    nextImageReady,
    nextImageReadyId,
    lastFrameCacheHas,
    lastFrameWcCacheHas,
    isVideoReadbackAllowed,
    hopFrameReadyId = null,
  } = params;
  // Só cobre troca REAL de mídia (id diferente).
  if (!prev || !cur || prev.id === cur.id) return null;
  // 1. Saindo de IMAGEM: o freeze já tem a imagem anterior capturada (estática →
  // congelá-la é invisível; a troca vira corte de conteúdo pro que entra).
  if (prev.type === 'image' && heldCanvasReady) return 'heldCanvas';
  // 2. Vídeo→imagem com destino pré-carregado → nextImage (invisível). Vence o
  // last-frame porque mostra o DESTINO real (zero corte visível na chegada).
  if (
    prev.type === 'video' &&
    cur.type === 'image' &&
    nextImageReady &&
    nextImageReadyId === cur.id
  ) {
    return 'nextImage';
  }
  // 2b. CORTE no meio de um vídeo (hop do pulso): o frame capturado no instante do
  // corte é o que estava na tela → cobre sem "voltar no tempo". Só onde o readback
  // de <video> funciona (quem captura já filtra por plataforma).
  if (
    isVideoReadbackAllowed &&
    prev.type === 'video' &&
    hopFrameReadyId !== null &&
    hopFrameReadyId === prev.id
  ) {
    return 'hopFrame';
  }
  // 3. Saindo de VÍDEO com o ÚLTIMO frame (WebCodecs) do PRÓPRIO vídeo em cache.
  // Software decode → seguro em Tizen/webOS (não é readback do <video>, que o HW
  // overlay bloqueia). Cobre vídeo→vídeo e o vídeo→imagem sem pré-carga.
  if (prev.type === 'video' && lastFrameWcCacheHas(prev.id))
    return 'lastFrameWc';
  // 4. Saindo de VÍDEO com o último frame NATIVO (drawImage(<video>)) em cache. Só
  // onde o readback de <video> é permitido (Android/browser). Em Tizen/webOS o
  // frame vem de HW overlay → readback preto/crash → nunca 'lastFrame' (as rotas
  // de <img> heldCanvas/nextImage + 'lastFrameWc' seguem valendo).
  if (
    isVideoReadbackAllowed &&
    prev.type === 'video' &&
    lastFrameCacheHas(prev.id)
  ) {
    return 'lastFrame';
  }
  return null;
}

/**
 * Último recurso da capa: capturar o frame AGORA, do `<video>` que ainda está na tela.
 *
 * <p>As quatro fontes de {@link getFreezeCandidateName} dependem de algo ter sido preparado
 * ANTES da troca — a imagem que sai, a imagem de destino pré-carregada, o último frame em
 * cache. Numa troca em que a playlist inteira é substituída de uma vez, nada disso está
 * pronto para o item que entra: é o caso da **saída de gatilho**, e o resultado medido no Tab
 * A7 (2026-10-12) foi ~300ms de tela preta em TODA saída — e ~1s quando havia recusa de
 * decoder junto.</p>
 *
 * <p>Mas o frame que o usuário está vendo continua no elemento: o `src` só troca num efeito
 * POSTERIOR ao da capa. Então, quando não há fonte nenhuma e estamos saindo de um vídeo, dá
 * para capturar na hora — é a mesma captura que o hop do pulso já usa.</p>
 *
 * <p>Só onde o readback de `<video>` é permitido (Android/browser). Em Tizen/webOS o quadro
 * vive no plano HW e o readback volta preto — lá a ausência de capa continua sendo o
 * comportamento, e o still-mode do AVPlay é que cobre.</p>
 */
/**
 * O standby do motor A/B deve devolver o decoder porque um GATILHO entrou?
 *
 * <p>Medido no Tab A7 (2026-10-10/11): com o A/B ligado, o gatilho pedia um terceiro decoder
 * que o aparelho não tinha — 42 `InsufficientResources` e ~70s de tela preta até liberar
 * sozinho. O standby é inútil durante o gatilho (o motor agendado já faz early-return em
 * `isTriggerPlayback`, então nenhuma troca será preparada), e devolvê-lo é exatamente a folga
 * que falta.</p>
 *
 * <p>Não há estado a restaurar: ao sair do gatilho o `prepare()` da próxima troca recarrega o
 * standby normalmente.</p>
 */
/**
 * O `<video>` deve repetir NATIVAMENTE (atributo `loop`)?
 *
 * <p>Numa playlist de um item só, deixar o próprio elemento repetir evita um ciclo de
 * descarregar/recarregar a cada volta — no WebKit da TV isso custava re-decodificar o vídeo
 * inteiro. Por isso o `loop` existe.</p>
 *
 * <p><b>Mas ele não pode valer durante um gatilho que precisa TERMINAR.</b> Com `loop`, o
 * elemento nunca emite `ended`; sem `ended` não há avanço, sem avanço a playlist nunca dá a
 * volta, e `handleTriggerPlaylistComplete` — a única forma de o player encerrar um ONCE por
 * conta própria — nunca é chamada. Um gatilho ONCE de UM vídeo tocava para sempre, e o
 * player ficava dependendo exclusivamente do SSE de desativação do servidor chegar
 * (reportado pelo Renato no player web, 2026-10-14).</p>
 *
 * <p>No modo CONTINUOUS repetir é exatamente o desejado — lá o `loop` fica.</p>
 */
export function deveRepetirNativamente(params: {
  totalDeItens: number;
  tipo?: string;
  emGatilho: boolean;
  /** Modo do gatilho ativo; só CONTINUOUS pode repetir nativamente. */
  modoDoGatilho?: string | null;
}): boolean {
  const { totalDeItens, tipo, emGatilho, modoDoGatilho } = params;
  if (totalDeItens !== 1 || tipo !== 'video') return false;
  if (emGatilho && modoDoGatilho !== 'CONTINUOUS') return false;
  return true;
}

export function deveSoltarStandbyNoGatilho(params: {
  /** O motor A/B está operando? Sem ele não existe standby a devolver. */
  abLigado: boolean;
  emGatilho: boolean;
  /** O elemento standby e o ativo — iguais (ou ausentes) significa que não há standby. */
  standby: { src?: string; currentSrc?: string } | null;
  ativo: unknown;
}): boolean {
  const { abLigado, emGatilho, standby, ativo } = params;
  if (!abLigado || !emGatilho) return false;
  if (!standby || standby === ativo) return false;
  // Sem fonte, não há decoder preso: mexer aqui só gastaria um `load()` à toa.
  return !!(standby.currentSrc || standby.src);
}

export function deveCapturarCapaNaHora(params: {
  /** O que {@link getFreezeCandidateName} já decidiu (null = nenhuma fonte pronta). */
  candidate: string | null;
  prevType?: string;
  prevId?: string | null;
  curId?: string | null;
  readbackPermitido: boolean;
}): boolean {
  const { candidate, prevType, prevId, curId, readbackPermitido } = params;
  if (candidate) return false; // já há fonte melhor, preparada com antecedência
  if (!readbackPermitido) return false;
  if (prevType !== 'video') return false; // de imagem não há o que capturar do <video>
  if (!prevId || !curId || prevId === curId) return false; // troca real de mídia
  return true;
}

/**
 * Decide se deve CAPTURAR o último frame do vídeo agora (Etapa B) — extraído dos
 * guards de `maybeCaptureLastFrame` pra teste puro. Regras: só Android/Browser
 * (Tizen/webOS têm HW overlay → readback preto), só vídeo, 1×/vídeo (has/capturing),
 * só nos últimos ~0.5s do fim EFETIVO (corte se cut-short, senão duração natural).
 * O `drawImage`/`toDataURL` em si fica no Stream (precisa de <video>/<canvas> reais).
 */
export function shouldCaptureLastFrame(params: {
  platform: string;
  mediaType: string | undefined;
  alreadyCached: boolean;
  alreadyCapturing: boolean;
  videoWidth: number;
  videoDuration: number;
  currentTime: number;
  shouldCutShort: boolean;
  scheduledDuration: number;
}): boolean {
  const {
    platform,
    mediaType,
    alreadyCached,
    alreadyCapturing,
    videoWidth,
    videoDuration,
    currentTime,
    shouldCutShort,
    scheduledDuration,
  } = params;
  if (platform === 'tizen' || platform === 'webos') return false;
  if (mediaType !== 'video') return false;
  if (alreadyCached || alreadyCapturing) return false;
  if (videoWidth === 0 || !Number.isFinite(videoDuration)) return false;
  const effEnd =
    shouldCutShort && scheduledDuration > 0 ? scheduledDuration : videoDuration;
  if (effEnd <= 0) return false;
  const remaining = effEnd - currentTime;
  // Só nos últimos ~0.5s (frame ≈ o final; salto imperceptível).
  if (remaining > 0.5 || remaining <= 0) return false;
  return true;
}

/**
 * Janela final (s) da captura CONTÍNUA do último frame via requestVideoFrameCallback.
 * O loop só fica ATIVO neste último trecho → custo zero no meio do vídeo. Não precisa
 * ser grande: uma vez dentro, o rVFC redesenha CADA frame apresentado até o fim, então
 * o último redesenho é o verdadeiro último frame. ~0.7s garante que o `timeupdate`
 * (~4×/s) caia na janela e arranque o loop bem antes dos frames finais.
 */
export const LAST_FRAME_CAPTURE_WINDOW_SEC = 0.7;

/**
 * Decide se o vídeo está DENTRO da janela final onde o último frame deve ser
 * capturado CONTINUAMENTE (rewind fix 2026-07-16) — extraído pra teste puro.
 *
 * Diferente de `shouldCaptureLastFrame` (captura 1× num limiar → frame de alguns ms
 * ANTES do fim → "rewind" perceptível em cena de ação), aqui a captura roda a CADA
 * frame apresentado (via `requestVideoFrameCallback`) enquanto dentro da janela, de
 * modo que o cache sempre guarde o frame MAIS RECENTE — no instante da troca é o
 * VERDADEIRO último frame (zero rewind).
 *
 * Mesmos gates do `shouldCaptureLastFrame`: só Android/browser (Tizen/webOS têm HW
 * overlay → readback preto; lá usa-se WebCodecs `lastFrameWc`), só vídeo, videoWidth
 * pintado, duração finita. Fim EFETIVO = corte (cut-short) ou duração natural. Fora
 * da janela retorna false (o chamador NÃO agenda/roda nada → custo zero no meio).
 */
export function isWithinLastFrameCaptureWindow(params: {
  platform: string;
  mediaType: string | undefined;
  videoWidth: number;
  videoDuration: number;
  currentTime: number;
  shouldCutShort: boolean;
  scheduledDuration: number;
}): boolean {
  const {
    platform,
    mediaType,
    videoWidth,
    videoDuration,
    currentTime,
    shouldCutShort,
    scheduledDuration,
  } = params;
  if (platform === 'tizen' || platform === 'webos') return false;
  if (mediaType !== 'video') return false;
  if (videoWidth === 0 || !Number.isFinite(videoDuration)) return false;
  const effEnd =
    shouldCutShort && scheduledDuration > 0 ? scheduledDuration : videoDuration;
  if (effEnd <= 0) return false;
  const remaining = effEnd - currentTime;
  if (remaining <= 0) return false;
  return remaining <= LAST_FRAME_CAPTURE_WINDOW_SEC;
}

/**
 * Decide se o `<video>` deve ser ESCONDIDO (visibility:hidden) enquanto o canvas
 * de freeze cobre a transição (FIX 2, 2026-07-16) — extraído pra teste puro.
 *
 * SÓ em Tizen/webOS: lá o HW overlay do vídeo pisca PRETO ao trocar de src e
 * renderiza ACIMA do canvas de freeze (overlays de HW ignoram z-index) → ~300ms
 * de preto. Escondendo o <video> enquanto o freeze está visível, o overlay some;
 * revela-se junto com o hide do freeze. Em Android/browser o z-index funciona e
 * esconder poderia introduzir flash → NUNCA esconde.
 */
export function shouldHideVideoForFreeze(
  platform: string,
  isFreezeFrameVisible: boolean,
): boolean {
  if (platform !== 'tizen' && platform !== 'webos') return false;
  return isFreezeFrameVisible;
}

/**
 * PHASE 1 — HOLD-FRAME (Tizen/webOS, video→img). Decide se o `<video>` que SAI deve
 * ser SEGURADO (mantido montado + visível, pausado no último frame) durante a
 * transição, em vez de desmontado/`.load()`/`removeAttribute('src')` — extraído pra
 * teste puro.
 *
 * Por quê: em Tizen/webOS o `<video>` vive no plano HW overlay, que (a) renderiza
 * ACIMA do plano gráfico (canvas/img — z-index ignorado) e (b) PISCA PRETO ao ser
 * derrubado (tear-down do src). Um `<video>` PAUSADO, porém, SEGURA seu último frame
 * na tela (confirmado no `SsspHoldProbe` na SSSP 6.5 — sem WebCodecs). Então, em vez
 * de LER o frame (WebCodecs, ausente na 6.5), só NÃO derrubamos o vídeo: pausa +
 * mantém visível segurando o frame, deixa a IMAGEM de destino pintar por baixo, e só
 * depois esconde/desmonta o vídeo → zero preto.
 *
 * SÓ video→img (a imagem NÃO usa o decoder de vídeo → sem conflito de decoder;
 * video→video precisa de um 2º elemento/decoder e é uma fase posterior).
 * Android/browser (readback funciona, z-index respeitado, sem flash de tear-down) →
 * NUNCA segura (false) — caminho intocado.
 */
export function shouldHoldVideoFrameForImage(params: {
  platform: string;
  currentType: string | undefined;
  nextType: string | undefined;
}): boolean {
  const { platform, currentType, nextType } = params;
  if (platform !== 'tizen' && platform !== 'webos') return false;
  return currentType === 'video' && nextType === 'image';
}

/**
 * Decide se, no BOOT, deve mostrar "Carregando" em vez de "Aguardando conteúdo"
 * (Renato 2026-07-05). Objetivo: matar o FLASH de NoContent (~2-3s) na janela
 * entre "1º sync OK" e "currentMedia setado" (scheduler/cache settling),
 * mostrando Carregando por até ~5s. Só NÃO adia (deixa o NoContent aparecer)
 * quando o estado é DEFINITIVO:
 *  - já tem mídia (irrelevante — renderiza o conteúdo);
 *  - player bloqueado / auth/registro pendente (estados autoritativos);
 *  - servidor confirmou biblioteca VAZIA (status success + !hasContent = admin
 *    realmente sem nada) → NoContent na hora, sem esperar o grace.
 * NÃO toca filtros temporais — preserva o NoContent legítimo (definitivo ou
 * depois do grace, ex.: tudo filtrado por horário/dia).
 */
export function shouldDeferNoContentForBoot(params: {
  hasCurrentMedia: boolean;
  isPlayerBlocked: boolean;
  authOrRegistrationPending: boolean;
  serverConfirmedEmpty: boolean;
  withinBootGrace: boolean;
}): boolean {
  const {
    hasCurrentMedia,
    isPlayerBlocked,
    authOrRegistrationPending,
    serverConfirmedEmpty,
    withinBootGrace,
  } = params;
  if (hasCurrentMedia) return false; // já tem o que mostrar
  if (isPlayerBlocked || authOrRegistrationPending) return false; // autoritativo
  if (serverConfirmedEmpty) return false; // admin realmente sem nada
  return withinBootGrace; // adia o NoContent só durante o grace de boot
}

/** FIX B — grace GENEROSO extra (s) somado à duração do slot (SSSP fraca). */
export const ADVANCE_WATCHDOG_SLOT_MARGIN_MS = 6000;

/**
 * FIX B — timeout (ms) do WATCHDOG DE AVANÇO: `max(floorMs, duraçãoDoSlot + 6s)`
 * (extraído pra teste puro). Slot sem duração conhecida → assume 10s. Grace
 * GENEROSO de propósito (TVs SSSP fracas demoram a decodificar) — a rede de
 * segurança só existe pra "nunca ficar preso PRA SEMPRE", não pra pular
 * conteúdo lento-mas-válido. Vide `shouldAdvanceWatchdogFire`.
 */
export function computeAdvanceWatchdogTimeoutMs(
  slotDurationSec: number | null | undefined,
  floorMs: number,
): number {
  const slotSec = slotDurationSec && slotDurationSec > 0 ? slotDurationSec : 10;
  return Math.max(floorMs, slotSec * 1000 + ADVANCE_WATCHDOG_SLOT_MARGIN_MS);
}

/**
 * FIX B — decide se o watchdog de avanço deve DISPARAR (extraído pra teste puro).
 * CONSERVADOR (decisão Renato): só dispara em STALL REAL no início — conteúdo
 * lento-mas-válido NUNCA pode ser pulado.
 *  - vídeo: nunca tocou (`hasEverPlayed=false`) E readyState < HAVE_CURRENT_DATA
 *    (2) E currentTime NÃO avançou (== 0). Se o readyState subiu (loadeddata/
 *    canplay) OU o currentTime avançou (timeupdate) → NÃO dispara. (Na prática
 *    esses eventos JÁ cancelam o timer no Stream; este é o guard final.)
 *  - imagem: só se ainda NÃO carregou (`imageLoaded=false`).
 * Vídeo sem elemento (`video=null`) conta como travado (nunca montou/decodificou).
 */
export function shouldAdvanceWatchdogFire(params: {
  mediaType: string;
  hasEverPlayed: boolean;
  videoReadyState: number | null;
  videoCurrentTime: number | null;
  imageLoaded: boolean;
}): boolean {
  const {
    mediaType,
    hasEverPlayed,
    videoReadyState,
    videoCurrentTime,
    imageLoaded,
  } = params;
  if (mediaType === 'video') {
    if (hasEverPlayed) return false;
    if (videoReadyState == null) return true; // sem <video> montado → travado
    // readyState subiu (tem frame) OU currentTime avançou → conteúdo válido,
    // apenas lento → NÃO pula.
    return videoReadyState < 2 && (videoCurrentTime ?? 0) <= 0;
  }
  // Imagem: travada se ainda não carregou.
  return !imageLoaded;
}

/**
 * Portão do watchdog de STALL DE DECODER mid-playback (Case 4 em `Stream/index.tsx`).
 * Só deixa rodar o recovery (seek+play / load) quando o vídeo está REALMENTE travando
 * no meio do playback.
 *
 * ⚠️ `paused` FECHA o portão: um vídeo pausado NÃO é stall de decoder. Pode ser pausa
 * intencional do app OU — no browser desktop — o Chrome pausando mídia **muda de aba em
 * background** pra economizar energia ("video-only background media was paused to save
 * power"). Rodar seek+play nesse caso briga com o navegador (que re-aborta o `play()`) e
 * vira um **loop de AbortError**. Stall de decoder REAL (Tab A7/HEVC, Tizen) mantém
 * `paused=false` (o `<video>` "acha" que está tocando, só não avança) — então o portão
 * continua aberto pra ele. Kiosk da frota é sempre foreground → nunca sofre o power-save.
 */
export function shouldRunStallRecovery(params: {
  hostHidden: boolean;
  cutShort: boolean;
  manualLoop: boolean;
  hasPlayedOrCanPlay: boolean;
  ended: boolean;
  paused: boolean;
}): boolean {
  return (
    !params.hostHidden &&
    !params.cutShort &&
    !params.manualLoop &&
    params.hasPlayedOrCanPlay &&
    !params.ended &&
    !params.paused
  );
}

/**
 * BOOT-BLACK LOAD-KICK (2026-08-17) — plataformas onde o load-kick roda.
 * Só Android/Browser: na Tizen/webOS o vídeo vai pelo AVPlay (plano HW), NÃO pelo
 * `<video>` do Chromium — a race do `applyVideoSource` (removeAttribute('src') →
 * NETWORK_EMPTY) que o kick corrige simplesmente não existe lá. Espelha o gate de
 * `shouldCaptureLastFrame`/`isWithinLastFrameCaptureWindow` (que EXCLUEM tizen/webos).
 */
export function isLoadKickPlatform(platform: string): boolean {
  return platform === 'android' || platform === 'browser';
}

/**
 * VÍDEO-ENGINE / DECODER (2026-09-14) — plataformas que rodam o `<video>` do Chromium
 * DENTRO de uma WebView Android e portanto compartilham as MESMAS restrições de decoder
 * HW (OMX/CMA no Tab A7): a frota Capacitor (`android`) E o **Efflux Embedded**
 * (`embedded`, WebView Android in-process do app host). Ambas precisam do teardown/
 * recovery de decoder (`releaseAndroidVideoDecoder`, reload em waiting/stall) que a
 * Tizen/webOS (AVPlay, plano HW) e o browser desktop não precisam.
 *
 * O embedded era tratado como `!== 'android'` → PULAVA todo o teardown do decoder →
 * o renderer da WebView vazava recursos nativos (decoder/GPU/CMA — invisível ao heap
 * JS) durante decode sustentado e a OS matava o renderer (`onRenderProcessGone`) → o
 * app recarregava e o server inferia `silent_crash`. Confirmado no A7 (SM-T505): 17/20
 * crashes tocando o vídeo de 6min/720p do fallback, heap sempre 10MB. A frota Android
 * NÃO tinha esses crashes justamente porque roda este teardown. Vide FEATURE-EMBEDDED.
 */
export function isAndroidWebView(platform: string): boolean {
  return platform === 'android' || platform === 'embedded';
}

/**
 * BOOT-BLACK LOAD-KICK (2026-08-17) — decide se o load-kick deve DISPARAR agora
 * (extraído pra teste puro; espelha `shouldAdvanceWatchdogFire`).
 *
 * Causa raiz (só Android): `applyVideoSource` faz `await releaseAndroidVideoDecoder`
 * (que faz `removeAttribute('src')` → src VAZIO) e, se o effect for cancelado DURANTE
 * o await (tempestade de re-renders do boot/reload), bail deixando o `<video>` em
 * `src=""` (NETWORK_EMPTY) → preto eterno até o skip de ~16s. O kick RE-RESOLVE a url
 * e RE-APLICA o src ~0,5s depois, SEM pular o slot.
 *
 * Guard SUPER específico (nunca age em playback normal — um vídeo saudável já passou
 * de readyState=0 em ~150ms):
 *  - `stillCurrent`: o slot AINDA é o que armou o kick (troca de mídia aborta);
 *  - `hasEverPlayed=false`: nunca tocou (playback normal já passou disso);
 *  - `<video>` existe (`videoReadyState != null`), readyState === 0 (nunca decodificou
 *    1 frame) E currentTime === 0.
 * Sem `<video>` montado (`videoReadyState == null`) → NÃO dispara (não há src a
 * re-aplicar — diferente do advance watchdog, que trata `null` como travado).
 */
export function shouldLoadKickFire(params: {
  stillCurrent: boolean;
  hasEverPlayed: boolean;
  videoReadyState: number | null;
  videoCurrentTime: number | null;
}): boolean {
  const { stillCurrent, hasEverPlayed, videoReadyState, videoCurrentTime } =
    params;
  if (!stillCurrent) return false;
  if (hasEverPlayed) return false;
  if (videoReadyState == null) return false; // sem <video> → nada a re-aplicar
  return videoReadyState === 0 && (videoCurrentTime ?? 0) === 0;
}

/** Campos mínimos pra timeline/zonas (StreamMediaItem ou PlaylistItem). */
export interface ZoneTimelineItem {
  zonaId?: number;
  campanhaId?: number;
  duration?: number;
  startOffsetSeconds?: number;
}

export function activeZonesForCampaign(
  zones: SyncZone[],
  playlist: ZoneTimelineItem[],
  campanhaId: number | undefined,
): SyncZone[] {
  if (campanhaId == null || zones.length === 0) return [];
  const ids = new Set<number>();
  for (const item of playlist) {
    if (item.zonaId != null && item.campanhaId === campanhaId) {
      ids.add(item.zonaId);
    }
  }
  return zones.filter((z) => ids.has(z.id));
}

export function zoneGroupMaxDurationSec(
  playlist: ZoneTimelineItem[],
  activeZones: SyncZone[],
): number {
  let globalMax = 0;
  for (const zone of activeZones) {
    let seqOff = 0;
    for (const item of playlist.filter((i) => i.zonaId === zone.id)) {
      const inSec = item.startOffsetSeconds ?? seqOff;
      const dur = item.duration || 10;
      const outSec = inSec + dur;
      seqOff = outSec;
      if (outSec > globalMax) globalMax = outSec;
    }
  }
  return globalMax;
}

/**
 * Deve PULAR o load do AVPlay por url-lag? (decisão pura — FIX 2, 2026-07-27)
 *
 * <p>PROBLEMA REAL. `mediaId` e `url` chegam em ticks DIFERENTES (a url vem do `usePlayableUrl`,
 * async). Quando o slot troca de conteúdo, o `mediaId` muda 1 tick ANTES da url → o efeito
 * dispararia com a url do vídeo ANTERIOR e o recarregaria à toa (2× stop→open→prepare→play =
 * dobro do preto no swap). Pular nesse caso é CORRETO.
 *
 * <p>⚠️ FIX 3 (2026-07-27, preto video→vídeo na 1.0.304): a variante baseada em `slotKey`
 * ("slot avançou → NUNCA pula") REGREDIU o url-lag. Motivo: o url-lag acontece JUSTAMENTE no
 * avanço de slot — quando o slot troca A→B, o `usePlayableUrl` (async) segura a url STALE (a do A)
 * por 1 tick antes de resolver pro B. Com "slot avançou → carrega sempre", o efeito CARREGAVA a
 * url stale (A) e logo depois a real (B) = **double-load**: o load do A abre+prepara, o load do B
 * troca o `cur`, o `afterPrepare(A)` aborta sem play (churn: plays<<opens), e o 2º still-mode cai
 * num plano sem frame → **PRETO no video→vídeo**. Medido na bancada 9.0: opens 763 / plays 449.
 *
 * <p>O "caso espelho" que motivou a variante slotKey (2 itens DIFERENTES apontando pro MESMO
 * conteúdo → mediaId diferente + url igual → freeze) **NÃO EXISTE**: no transform o id do item é
 * `file.id` (id do CONTEÚDO) — mesmo conteúdo = MESMO mediaId. Então `[pasta,pasta]` cai no caso
 * "mediaId IGUAL" abaixo (não pula → recarrega/reinicia) e não trava. O re-disparo do efeito no
 * avanço vem do `slotKey` nas DEPS do effect (em AvplayVideo), não desta decisão.
 *
 * <p>REGRA (= 1.0.302, conhecido-bom): pula SÓ quando a url ainda é a que JÁ carregamos e o
 * conteúdo mudou (url-lag). Vale inclusive no avanço de slot. `slotKey` fica no schema por
 * compat com o call site, mas a decisão não depende dele.
 */
export function shouldSkipAvplayLoad(p: {
  url: string;
  mediaId: string | null;
  /** Índice do slot atual — mantido no schema por compat; a decisão NÃO depende dele (vide FIX 3). */
  slotKey?: number | undefined;
  lastUrl: string;
  lastMediaId: string | null;
  lastSlotKey?: number | undefined;
}): boolean {
  // url-lag: a url ainda é a do conteúdo ANTERIOR (stale) e o conteúdo mudou → espera a url
  // atualizar (não recarrega a url velha). Mesmo conteúdo (mediaId igual) NÃO pula → reinicia.
  return p.url === p.lastUrl && p.mediaId !== p.lastMediaId;
}

/**
 * Chave de identidade de uma EXIBIÇÃO de slot, pro dedup do `slot_started`.
 * `null` quando não há contexto de slot (Tier 4 — analytics de slot não roda).
 */
export function slotExhibitionKey(
  ctx: { slotIndex: number; contentId: string } | null,
): string | null {
  return ctx ? `${ctx.slotIndex}:${ctx.contentId}` : null;
}

/**
 * Deve emitir `slot_started`? (decisão pura — 2026-07-27)
 *
 * <p>PROBLEMA MEDIDO. O efeito que emite `slot_started` tem como dependência
 * `streamStore.currentMedia`, que é um computed do MobX devolvendo `playlist[currentIndex]` — uma
 * REFERÊNCIA. Cada `setPlaylist` recria os itens (`fromPlaylistItem`), então a referência muda
 * mesmo quando o conteúdo é idêntico e o efeito re-dispara. Na bancada isso virou um churn de
 * `slot_started slotIndex=0` **de 2 em 2 segundos**, sem nenhum `open` de AVPlay correspondente:
 * evento de analytics sendo gravado à toa, inflando o IndexedDB e queimando escrita.
 *
 * <p>🔴 O QUE NÃO PODE ACONTECER. Deduplicar só por slot QUEBRARIA A CONTAGEM DE EXIBIÇÕES: quando
 * o loop dá a volta e o mesmo slot toca de novo, aquela é uma exibição REAL e conta pro mínimo
 * contratual do cliente (R1). Suprimir viraria subcontagem — inaceitável.
 *
 * <p>REGRA. Uma exibição por `slot_started`. Emite quando a chave da exibição MUDA, ou quando a
 * exibição anterior JÁ TERMINOU (`slot_completed` limpa a última chave) — aí o mesmo slot tocando
 * outra vez emite normalmente. Só o repique CONSECUTIVO da mesma exibição, sem término no meio, é
 * suprimido: exatamente o churn.
 */
export function shouldEmitSlotStarted(p: {
  key: string | null;
  lastEmittedKey: string | null;
}): boolean {
  if (p.key == null) return false; // sem contexto de slot (Tier 4)
  return p.key !== p.lastEmittedKey;
}

/* ────────────────────────────────────────────────────────────────────────────
 * ZONAS / ORIENTAÇÃO CUSTOMIZADA — predicados puros (extraídos do Stream em
 * 2026-08-24). Guardam a série de fixes de transição/escala das zonas (Carlos):
 * capa na fronteira zona→fullscreen, exceção do gate AVPlay pra vídeo de zona,
 * e a escala "stretch" do layout multi-zona / fullscreen custom.
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Capa na FRONTEIRA de zona (zona→fullscreen). Espelha o override inline do
 * `useLayoutEffect`: quando a decisão-base ({@link getFreezeCandidateName}) não
 * achou candidato E estamos SAINDO de conteúdo de ZONA (layout multi-zona) com
 * o composite das zonas já capturado no heldCanvas (no fim do slot), a capa é
 * `'heldCanvas'`. Sem isso a fronteira zona→fullscreen cairia no preto
 * (BlackTransition), DIFERENTE das trocas entre conteúdos comuns.
 *
 * <p>`prevZonaId != null` = o conteúdo que SAIU pertencia a uma zona. Só sobrepõe
 * quando a base falhou (`!baseCandidate`) — nunca rebaixa um candidato já achado.
 */
export function resolveZoneAwareFreezeCandidate(params: {
  baseCandidate: FreezeCandidate;
  prevZonaId: number | null | undefined;
  heldCanvasReady: boolean;
}): FreezeCandidate {
  const { baseCandidate, prevZonaId, heldCanvasReady } = params;
  if (!baseCandidate && prevZonaId != null && heldCanvasReady) {
    return 'heldCanvas';
  }
  return baseCandidate;
}

/**
 * Deve PULAR o cover DOM ao SAIR de um vídeo? (gate do AVPlay + exceção de zona)
 *
 * <p>Sob AVPlay, sair de um vídeo fullscreen deixa o **plano HW** (still-mode)
 * cobrindo a tela → pula o cover DOM (evita a "piscada do conteúdo anterior").
 *
 * <p>⚠️ EXCEÇÃO ZONA (2026-08-24): vídeo DE ZONA toca no `<video>` DOM do
 * `ZonePlayer`, **não** no plano AVPlay. O still-mode do plano HW não está
 * segurando nada → sem o cover DOM, sair de zona-com-vídeo na Tizen fica
 * **preto garantido**. Logo, só pula quando o vídeo que saiu NÃO era de zona
 * (`prevZonaId == null`).
 */
export function shouldSkipDomCoverOnVideoExit(params: {
  avplayActive: boolean;
  prevType: string | undefined;
  prevZonaId: number | null | undefined;
}): boolean {
  const { avplayActive, prevType, prevZonaId } = params;
  return avplayActive && prevType === 'video' && prevZonaId == null;
}

/**
 * Escala "stretch" (X/Y independentes) de um retângulo do espaço de LAYOUT pro
 * espaço da TELA — usada no layout multi-zona e no fullscreen de orientação
 * customizada (2026-08-24). Preenche a tela inteira (zero borda preta).
 *
 * <p>⚠️ ATENÇÃO (divergência do guardrail `renderizacao-conteudo`): este é o
 * stretch **CRU** (fill, sem o clamp de 10% do `useViewportScale`). Pra layout
 * com aspecto muito diferente da tela, distorce mais que o conteúdo comum
 * (que letterboxa o excedente > 10%). É o comportamento atual das zonas — este
 * teste TRAVA isso pra a mudança pra "clamped" ser uma decisão consciente.
 *
 * <p>Guarda divisão por zero: layout 0 → escala 0 (retângulo colapsa; evita
 * `NaN`/`Infinity` que o `cw/lw` cru produziria).
 */
export function computeStretchRect(params: {
  x: number;
  y: number;
  w: number;
  h: number;
  layoutWidth: number;
  layoutHeight: number;
  containerWidth: number;
  containerHeight: number;
}): { left: number; top: number; width: number; height: number } {
  const {
    x,
    y,
    w,
    h,
    layoutWidth,
    layoutHeight,
    containerWidth,
    containerHeight,
  } = params;
  const sx = layoutWidth > 0 ? containerWidth / layoutWidth : 0;
  const sy = layoutHeight > 0 ? containerHeight / layoutHeight : 0;
  return { left: x * sx, top: y * sy, width: w * sx, height: h * sy };
}

/**
 * `drawImage(<video>)` (readback do plano de vídeo) é permitido? Em **Tizen/webOS**
 * o readback do `<video>` no WebKit volta PRETO (HW overlay / DRM protegido) →
 * proibido; só `<img>` é seguro. FONTE ÚNICA da decisão — antes recomputada
 * inline em 2 lugares do Stream (o efeito de freeze e o `captureZonesToHeldCanvas`
 * das zonas). Consolidada aqui (2026-08-24) pra não divergir.
 */
export function isVideoReadbackAllowedForPlatform(platform: string): boolean {
  return !(platform === 'tizen' || platform === 'webos');
}

/**
 * ANTI-RE-DECODE (loop de 1 imagem — fix Carlos b574c10). O effect "Image changed"
 * re-roda a cada volta do loop (dep em `currentItemStartedAt`, que muda a cada avanço).
 * Numa playlist de 1 slot é SEMPRE a MESMA imagem: se ela já está decodificada na tela,
 * remontar o BlackTransition forçaria o WebKit Tizen a re-decodificar o bitmap
 * (~13MB nativos) a CADA volta → dreno de memória nativa. Este predicado decide
 * "reusar a imagem já pintada" (só re-armar o avanço, sem re-decode).
 *
 * FONTE ÚNICA da decisão inline do Stream. Só reusa quando é o MESMO id E a `<img>`
 * de fato terminou de decodificar (`complete` + `naturalWidth>0`). `displayedImageId`
 * null (nunca exibiu) ou id diferente → NÃO reusa (caminho normal de decode).
 */
export function shouldReuseDecodedImageOnLoop(p: {
  displayedImageId: string | null;
  currentMediaId: string;
  imgComplete: boolean;
  imgNaturalWidth: number;
}): boolean {
  return (
    p.displayedImageId != null &&
    p.displayedImageId === p.currentMediaId &&
    p.imgComplete &&
    p.imgNaturalWidth > 0
  );
}

/**
 * ANTI-REALLOC do freeze-canvas (loop de 1 imagem — fix Carlos b574c10).
 * `captureImageToHeldCanvas` reatribui `canvas.width/height` (realoca o backing store
 * nativo/GPU) + `drawImage` 1440×2304 — caro. Se o canvas JÁ segura ESTA imagem
 * (mesma `currentSrc`, e já tem um frame válido), pular evita o churn de memória
 * nativa no WebKit Tizen a cada volta. Invalida (heldSrc=null) nos caminhos de
 * vídeo/zona que sobrescrevem o canvas — aí uma volta legítima de playlist mista
 * re-captura normalmente.
 */
export function shouldSkipHeldCanvasCapture(p: {
  heldReady: boolean;
  heldSrc: string | null;
  currentSrc: string;
}): boolean {
  return p.heldReady && p.heldSrc === p.currentSrc;
}

/**
 * Decide se um `onError` de `<img>` merece UMA nova tentativa antes de virar erro real.
 *
 * <p><b>Phantom error (2026-09-22).</b> Quando o `usePlayableUrl` resolve o cache logo depois do
 * mount, o Chrome ABORTA o load remoto pendente e dispara `onError` — para um arquivo que está
 * perfeito. Medido no tablet: MD5 idêntico ao do servidor, `fetch` 200 pelo `_capacitor_file_` e
 * decodificação 1920×1080 dentro do próprio WebView, e ainda assim o evento vinha.</p>
 *
 * <p>O guard antigo (`target.src !== expectedSrc`) NÃO cobre esse caso: quando o evento é
 * despachado, `target.src` já foi atualizado para a URL nova, então as duas batem e o erro passa
 * — pintando o overlay vermelho e ABANDONANDO o slot.</p>
 *
 * <p>O único sinal confiável aqui é comportamental: <b>phantom some na recarga; arquivo quebrado
 * não some</b>. Por isso damos exatamente UMA tentativa por `src`; se a segunda falhar, é erro de
 * verdade (arquivo de cache corrompido) e deve ser reportado como antes.</p>
 *
 * @param expectedSrc      URL que o `<img>` deveria estar carregando (cache ou remoto)
 * @param alreadyRetriedSrc último `src` que já consumiu a tentativa extra (`null` = nenhum)
 */
export function shouldRetryImageOnce(
  expectedSrc: string | null | undefined,
  alreadyRetriedSrc: string | null,
): expectedSrc is string {
  if (!expectedSrc) return false;
  return alreadyRetriedSrc !== expectedSrc;
}

/**
 * Rebaixa uma URL remota com CORS ignorando o cache HTTP, para que a próxima carga com
 * `crossOrigin="anonymous"` encontre uma cópia que traga `Access-Control-Allow-Origin`.
 *
 * <p><b>Cache envenenado (2026-10-19).</b> O R2 não responde `Vary: Origin`. Se a mesma URL
 * presigned já foi baixada SEM CORS no mesmo site (um `<img>` comum, um poster, o admin em
 * `admin.efflux.media` no mesmo navegador), o Chrome guarda essa cópia sem o cabeçalho e o
 * `<img crossOrigin>` do Stream falha em poucos ms. Medido no player web 1.0.478: o gatilho de
 * imagem era pulado na hora; depois de um `fetch` com `cache: 'reload'` a imagem carregou.</p>
 *
 * <p>Best-effort: nunca lança. Lê o corpo inteiro para a resposta nova ficar no cache.</p>
 */
export async function refreshCorsCacheEntry(url: string): Promise<void> {
  try {
    const response = await fetch(url, { mode: 'cors', cache: 'reload' });
    await response.blob();
  } catch {
    // A nova tentativa do <img> decide; se falhar de novo, o chamador pula o item.
  }
}

// ─── Pulso de sincronia: cortar ou deixar terminar (2026-09-30) ──────────────
//
// Contexto: sob sync canônico quem avança o player é o pulso, não o fim do item.
// Como `playback_complete` (a impressão) só nasce no fim natural, um player em
// grupo exclusivo gerava ZERO impressão — medido em prod no
// EMBEDDED-B4AF3DE5BBB8A740 (2026-09-29): 3 syncs, 0 eventos de analytics,
// enquanto todo player sem sync mandava 9–14 na mesma janela.

export interface VideoProgressSnapshot {
  paused: boolean;
  readyState: number;
  duration: number;
  currentTime: number;
}

/**
 * O pulso deve ADIAR o pulo e deixar o item terminar sozinho?
 *
 * <p>Só quando terminar naturalmente leva ao MESMO destino que pular levaria — ou
 * seja, quando o alvo já é o próximo item. Aí esperar custa décimos e faz o `ended`
 * disparar, o que produz a impressão. Se o alvo estiver mais longe, pula na hora:
 * a guarda nunca atrasa uma correção grande.</p>
 *
 * <p><b>Nunca trava:</b> `alreadyDeferred` limita o adiamento a UMA rodada por item.
 * Vídeo parado no fim (readyState baixo, pausado, ou que simplesmente não termina)
 * volta a ser pulado no pulso seguinte.</p>
 */
export function shouldLetItemFinish(p: {
  targetIndex: number;
  currentIndex: number;
  playlistLength: number;
  isVideo: boolean;
  video: VideoProgressSnapshot | null;
  /** Já adiamos este item uma vez? */
  alreadyDeferred: boolean;
  maxRemainingSeconds: number;
}): boolean {
  if (p.alreadyDeferred) return false;
  if (!p.isVideo || !p.video) return false;
  if (p.playlistLength <= 0) return false;
  if (p.targetIndex !== (p.currentIndex + 1) % p.playlistLength) return false;

  const { paused, readyState, duration, currentTime } = p.video;
  if (paused || readyState < 3) return false; // não está andando → não espere
  if (!Number.isFinite(duration) || duration <= 0) return false;

  return duration - currentTime <= p.maxRemainingSeconds;
}

/**
 * Fração do item que apareceu na tela, em tempo de RELÓGIO.
 *
 * <p>Não use `video.currentTime`: sob sync o player ENTRA no item já pela metade, e a
 * posição diria "exibiu inteiro" quando exibiu metade.</p>
 *
 * <p>Sem âncora (ou âncora de outro item) → `0`: preferimos perder uma impressão a
 * inventar uma.</p>
 */
export function shownRatio(p: {
  anchor: { mediaId: string; desdeMs: number } | null;
  mediaId: string;
  plannedSeconds: number;
  nowMs: number;
}): number {
  if (!p.anchor || p.anchor.mediaId !== p.mediaId) return 0;
  if (!(p.plannedSeconds > 0)) return 0;
  const shown = (p.nowMs - p.anchor.desdeMs) / 1000;
  if (!(shown > 0)) return 0;
  return shown / p.plannedSeconds;
}

/**
 * Velocidade de reprodução que corrige uma deriva PEQUENA sem salto — decisão pura.
 *
 * <p>O pulso de sync só sabia corrigir com seek, e seek só compensa para erro grande: ele
 * re-buferiza, pode re-inicializar o decoder e aparece como engasgo. Então tudo abaixo do
 * limiar de seek ficava sem correção nenhuma — e é justamente essa faixa que se enxerga como
 * "milissegundos de atraso entre um player e outro".</p>
 *
 * <p>Aqui o instrumento é outro: acelerar/desacelerar de leve por alguns segundos. O ajuste é
 * proporcional ao erro (constante de tempo {@code recoverSeconds}) e saturado em
 * {@code maxAdjust}, então nunca vira uma mudança audível/visível de ritmo.</p>
 *
 * <p>A zona morta existe porque o próprio relógio tem ruído: sem ela o player ficaria
 * oscilando em torno do alvo, trocando de velocidade a cada pulso sem nunca assentar.</p>
 *
 * @param erroSegundos  positivo = o player está ATRASADO (precisa acelerar)
 * @returns a velocidade a aplicar; igual a {@code baseRate} dentro da zona morta
 */
export type SeekPlan =
  | { action: 'none'; reason: string }
  | { action: 'seek'; seekTo: number }
  | { action: 'rate' };

/**
 * O que o pulso faz com um vídeo fora de posição — decisão pura, com as guardas que
 * faltavam e que viravam "travamento" (medido no tablet, out/2026).
 *
 * <p>Sem guarda, em aparelho lento o ciclo era: seek → `waiting` → `canplay` → o tempo gasto
 * no seek vira erro de novo → novo seek a cada 2,5s, sem nunca assentar. As guardas:</p>
 * <ul>
 *   <li><b>seek em voo</b> (`seeking`) ou vídeo sem dados (`readyState < 3`): não mexe.</li>
 *   <li><b>histerese</b>: logo depois de um seek a tolerância dobra por uma janela — o
 *   engasgo do próprio seek não dispara o seguinte.</li>
 *   <li><b>latência compensada</b>: o alvo é somado ao tempo que o ÚLTIMO seek levou, pra
 *   cair no lugar certo quando o vídeo voltar a tocar, não onde o lugar certo ERA.</li>
 *   <li>perto do fim não há seek (empurraria pro `ended`); o resto é correção por velocidade.</li>
 * </ul>
 *
 * @param p.erroSegundos positivo = atrasado (alvo à frente da posição atual)
 */
export function resolveSeekPlan(p: {
  erroSegundos: number;
  targetOffsetS: number;
  durationS: number;
  seeking: boolean;
  readyState: number;
  sinceLastSeekMs: number;
  seekLatencyMs: number;
  toleranceS: number;
  postSeekToleranceS: number;
  postSeekWindowMs: number;
}): SeekPlan {
  if (!Number.isFinite(p.erroSegundos))
    return { action: 'none', reason: 'erro inválido' };
  if (p.seeking) return { action: 'none', reason: 'seek em voo' };
  if (p.readyState < 3)
    return { action: 'none', reason: 'sem dados (readyState < 3)' };
  const recentSeek =
    p.sinceLastSeekMs >= 0 && p.sinceLastSeekMs < p.postSeekWindowMs;
  const tolerance = recentSeek ? p.postSeekToleranceS : p.toleranceS;
  if (Math.abs(p.erroSegundos) <= tolerance) return { action: 'rate' };
  // Seek perto do fim empurraria o vídeo pro `ended` — deixa terminar sozinho.
  if (!(p.targetOffsetS < p.durationS - 0.5))
    return { action: 'none', reason: 'perto do fim' };
  const compensado = p.targetOffsetS + Math.max(0, p.seekLatencyMs) / 1000;
  const seekTo = Math.min(compensado, p.durationS - 0.5);
  return { action: 'seek', seekTo };
}

/**
 * Pré-carregar o PRÓXIMO vídeo num 2º `<video>` oculto? Só onde há decoder sobrando:
 * navegador e desktop (WebView2/Chromium com decode por software à vontade). Android fica
 * de fora de propósito — no Tab A7 um 2º consumidor do OMX já derrubou o decoder pra
 * software (vide plataformas-suportadas.md, "race decoder OMX"); Tizen travou com qualquer
 * `<video preload>` paralelo (v1.0.30/32). O env `VITE_NEXT_VIDEO_PRELOAD` força on/off.
 */
export function shouldPreloadNextVideo(
  platform: string,
  envFlag: string | undefined = import.meta.env.VITE_NEXT_VIDEO_PRELOAD,
): boolean {
  if (envFlag === 'false') return false;
  if (envFlag === 'true') return platform !== 'tizen' && platform !== 'webos';
  return (
    platform === 'browser' || platform === 'windows' || platform === 'linux'
  );
}

export function resolveSyncPlaybackRate(p: {
  erroSegundos: number;
  baseRate: number;
  deadbandSeconds: number;
  maxAdjust: number;
  recoverSeconds: number;
}): number {
  if (!Number.isFinite(p.erroSegundos)) return p.baseRate;
  if (Math.abs(p.erroSegundos) <= p.deadbandSeconds) return p.baseRate;
  const bruto = p.erroSegundos / p.recoverSeconds;
  const ajuste = Math.max(-p.maxAdjust, Math.min(p.maxAdjust, bruto));
  return p.baseRate * (1 + ajuste);
}

/** Por que o pulso de sincronismo não agiu nesta rodada. */
export type SyncPulseSkipReason =
  | 'no-anchor'
  | 'not-playing'
  | 'loading'
  | 'trigger'
  | 'cooldown';

export type SyncPulseGate =
  | { run: true }
  | { run: false; reason: SyncPulseSkipReason };

/**
 * O pulso de sincronismo canônico deve agir AGORA?
 *
 * <p>O pulso corrige a posição do player contra a régua canônica
 * ({@code (agora − época) % loop}). Esta porta reúne, num lugar testável, todas as
 * condições em que ele precisa ficar quieto.</p>
 *
 * <p><b>🔴 `trigger` — o motivo pelo qual esta função existe (2026-10-10).</b> Um
 * gatilho SUBSTITUI a playlist inteira ({@code trigger.store} chama
 * {@code setPlaylist(conteúdoDoGatilho, …, isTrigger=true)}). O pulso não sabia disso
 * e seguia calculando a posição canônica — só que com as durações do GATILHO no lugar
 * das do loop. Concluía que o player estava fora de posição e arrancava o conteúdo de
 * volta. O gatilho puxava, o pulso puxava de volta.</p>
 *
 * <p>Cada arranque desses derruba e recria o decoder de vídeo. Num Tab A7, já apertado
 * de CMA, isso esgota o hardware em poucos minutos:
 * {@code Failed to initialize OMX.qcom.video.decoder.avc, error -12}. Medido no
 * aparelho em 2026-10-10: 22 falhas de alocação em 7 minutos, começando no disparo do
 * gatilho. Sintoma pro operador: tela preta, conteúdo travado, e intermitente — porque
 * depende da corrida entre a duração do gatilho, o tick do pulso e o cooldown.</p>
 *
 * <p>O motor agendado A/B, no mesmo arquivo, já tinha este guard; o pulso ficou sem.</p>
 *
 * <p><b>Voltar sincronizado é de graça.</b> Ao fim do gatilho a playlist normal é
 * restaurada e {@code resolveInitialIndex} recalcula a posição canônica — a fórmula é
 * ABSOLUTA, não incremental, então o player pousa onde deveria estar naquele instante.
 * Vale igual pra um player só ou pro grupo exclusivo inteiro: todos computam a mesma
 * conta com a mesma época e o mesmo relógio do servidor.</p>
 */
export function resolveSyncPulseGate(p: {
  /** Tem `playbackStartedAt` (época canônica) no item atual? */
  hasAnchor: boolean;
  isPlaying: boolean;
  /** Transição em curso — deixa o caminho normal (canplay/imageLoad) assentar. */
  isLoading: boolean;
  isTriggerPlayback: boolean;
  sinceLastActionMs: number;
  cooldownMs: number;
}): SyncPulseGate {
  if (!p.hasAnchor) return { run: false, reason: 'no-anchor' };
  if (!p.isPlaying) return { run: false, reason: 'not-playing' };
  if (p.isLoading) return { run: false, reason: 'loading' };
  // ANTES do cooldown de propósito: o gatilho não é "espere um pouco", é "não é seu".
  if (p.isTriggerPlayback) return { run: false, reason: 'trigger' };
  if (p.sinceLastActionMs < p.cooldownMs) return { run: false, reason: 'cooldown' };
  return { run: true };
}

/**
 * Vale tentar abrir o vídeo DE NOVO antes de pular o item?
 *
 * <p>Medido no tablet (2026-10-15): o `<video>` falha ao abrir um arquivo do cache
 * local com `MEDIA_ERR_SRC_NOT_SUPPORTED` ("FFmpegDemuxer: open context failed") de
 * forma transitória — o mesmo arquivo abre 150 ms depois. Sempre no vídeo que vem
 * logo após uma IMAGEM. Pular na hora virava o glitch visível: o próximo item
 * aparecia por ~130 ms e o pulso de sincronismo trazia o player de volta.</p>
 *
 * <p>Só vale pra código 4 (fonte não suportada / não abriu) num item que ainda NÃO
 * tocou nada, e UMA vez por mídia — na segunda falha o pulo de sempre resolve.
 * Código 3 (decode) tem tratamento próprio (freeze); 2 (rede) segue pulando.</p>
 */
export function shouldRetryVideoOpen(p: {
  errorCode: number | undefined;
  hasEverPlayed: boolean;
  mediaId: string | undefined;
  /** Id da mídia pra qual a retentativa já foi feita (ref do chamador). */
  retriedForMediaId: string | null;
}): boolean {
  if (p.errorCode !== 4) return false;
  if (p.hasEverPlayed) return false;
  if (!p.mediaId) return false;
  return p.retriedForMediaId !== p.mediaId;
}
